/* ============================================================
   storage — properties.

   What a thing actually is. Three shapes behind one dialog:

     a file      its own facts, known already from the listing
     a folder    walked on the server, because "how big is this
                 folder" is the question a listing cannot answer
     a selection the same walk, summed

   Sizes appear twice on purpose — "1.4 GB" is what you want to
   read and "1,503,238,553 bytes" is what you want when you are
   working out whether something will fit.
   ============================================================ */

import React, { useEffect, useState } from 'react';
import { Icon, fileIconName, fmtBytes, fmtDate } from './lib';
import { api } from './api';

const exact = (n) => (Number.isFinite(n) ? `${n.toLocaleString()} bytes` : '—');

function Rows({ items }) {
  return (
    <dl className="st-props">
      {items.filter(Boolean).map(([k, v, title]) => (
        <div key={k} className="st-prop">
          <dt>{k}</dt>
          <dd title={title || (typeof v === 'string' ? v : undefined)}>{v}</dd>
        </div>
      ))}
    </dl>
  );
}

export default function Properties({ token, target, me, drives, onClose }) {
  const [stats, setStats] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const kind = target.kind;                      // 'file' | 'folder' | 'selection'
  const driveLabel = (id) => drives.find((d) => d.id === id)?.label || id;

  useEffect(() => {
    if (kind === 'file') return undefined;
    const ctrl = new AbortController();
    setBusy(true); setError('');
    const call = kind === 'folder'
      ? api.folderStats(token, target.row.id, ctrl.signal)
      : api.batchStats(token,
        target.rows.filter((r) => r.kind === 'file').map((r) => r.id),
        target.rows.filter((r) => r.kind === 'folder').map((r) => r.id),
        ctrl.signal);
    call
      .then((d) => { setStats(d); setBusy(false); })
      .catch((e) => {
        if (e.name === 'AbortError') return;
        setError(e.message || 'Could not read this'); setBusy(false);
      });
    return () => ctrl.abort();
  }, [token, kind, target]);

  let title;
  let icon;
  let rows = [];

  if (kind === 'file') {
    const f = target.row.raw;
    title = f.original_name;
    icon = fileIconName(f.mime_type, f.original_name);
    rows = [
      ['Kind', f.mime_type || 'unknown'],
      ['Size', `${fmtBytes(f.size)}`, exact(f.size)],
      ['Exact', exact(f.size)],
      ['Where', f.path === '/' ? driveLabel(f.drive) : `${driveLabel(f.drive)} · ${f.path}`],
      ['Added by', f.owner_id === me?.id ? 'you' : f.owner_name || '—'],
      ['Added', fmtDate(f.created_at)],
      f.sha256 ? ['SHA-256', <code className="st-prop-hash">{f.sha256}</code>, f.sha256] : null,
    ];
  } else if (kind === 'folder') {
    const d = target.row;
    title = d.label;
    icon = d.secure ? 'folderLock' : 'folder';
    rows = [
      ['Kind', d.secure ? 'Secure folder' : 'Folder'],
      ['Contains', stats
        ? `${stats.files} file${stats.files === 1 ? '' : 's'}`
          + (stats.folders ? `, ${stats.folders} folder${stats.folders === 1 ? '' : 's'}` : '')
        : (busy ? 'counting…' : '—')],
      ['Size', stats ? fmtBytes(stats.bytes) : (busy ? '…' : '—'), stats ? exact(stats.bytes) : ''],
      stats?.bytes ? ['Exact', exact(stats.bytes)] : null,
      stats?.largest ? ['Largest', `${stats.largest.name} · ${fmtBytes(stats.largest.size)}`] : null,
      ['Where', d.path === '/' ? driveLabel(target.drive) : `${driveLabel(target.drive)} · ${d.path}`],
      ['Made by', d.mine ? 'you' : d.owner || '—'],
      ['Made', fmtDate(d.created_at)],
      stats?.newest ? ['Newest inside', fmtDate(stats.newest)] : null,
    ];
  } else {
    const n = target.rows.length;
    title = `${n} item${n === 1 ? '' : 's'}`;
    icon = 'check';
    const f = target.rows.filter((r) => r.kind === 'file').length;
    const d = n - f;
    rows = [
      ['Selected', [f && `${f} file${f === 1 ? '' : 's'}`, d && `${d} folder${d === 1 ? '' : 's'}`]
        .filter(Boolean).join(' and ')],
      stats ? ['Contains', `${stats.files} file${stats.files === 1 ? '' : 's'}`
        + (stats.folders ? `, ${stats.folders} folder${stats.folders === 1 ? '' : 's'} in total` : '')] : null,
      ['Size', stats ? fmtBytes(stats.bytes) : (busy ? 'adding up…' : '—'),
        stats ? exact(stats.bytes) : ''],
      stats?.bytes ? ['Exact', exact(stats.bytes)] : null,
      stats?.largest ? ['Largest', `${stats.largest.name} · ${fmtBytes(stats.largest.size)}`] : null,
      stats?.drives?.length ? ['Drives', stats.drives.map(driveLabel).join(', ')] : null,
      stats?.oldest ? ['Oldest', fmtDate(stats.oldest)] : null,
      stats?.newest ? ['Newest', fmtDate(stats.newest)] : null,
    ];
  }

  const owners = stats?.owners?.filter((o) => o.bytes > 0) || [];

  return (
    <div className="modal-backdrop st-backdrop" role="presentation"
         onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal st-modal st-props-modal" role="dialog" aria-modal="true"
           aria-label={`Properties of ${title}`}>
        <header className="st-props-head">
          <Icon name={icon} size={18} className={kind === 'file' ? 'st-icon-file' : 'st-icon-folder'}/>
          <h2 className="h3 st-props-title" title={title}>{title}</h2>
          <button type="button" className="iconbtn" aria-label="Close" onClick={onClose}>
            <Icon name="close" size={14}/>
          </button>
        </header>

        {error && <div className="alert alert--warn">{error}</div>}

        <Rows items={rows}/>

        {/* Who is using the space, when more than one person is. A single
            owner is already stated above and repeating it is noise. */}
        {owners.length > 1 && (
          <section className="st-props-owners">
            <span className="label">Space by person</span>
            {owners.map((o) => (
              <div key={o.id} className="st-props-owner">
                <span className="st-props-owner-name">{o.id === me?.id ? 'you' : o.name}</span>
                <span className="bar">
                  <span className="fill" style={{ width: `${(o.bytes / (stats.bytes || 1)) * 100}%` }}/>
                </span>
                <span className="tnum">{fmtBytes(o.bytes)}</span>
              </div>
            ))}
          </section>
        )}

        {/* A secure folder inside something you do not own is counted but not
            opened. Saying so beats a total that quietly does not add up. */}
        {stats?.withheld > 0 && (
          <p className="meta st-props-note">
            {stats.withheld} item{stats.withheld === 1 ? '' : 's'} inside a secure folder
            {stats.withheld === 1 ? ' is' : ' are'} not included — only their owner and admins
            can see what is in there.
          </p>
        )}

        <footer className="st-props-foot">
          <button type="button" className="btn btn--sm" onClick={onClose}>Done</button>
        </footer>
      </div>
    </div>
  );
}
